import { Badge, Button, Card, Input, Label, cn } from "@safeplate/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  Archive,
  CalendarClock,
  CheckSquare2,
  ClipboardList,
  Pencil,
  Plus,
  RefreshCw,
  SkipForward,
  X,
} from "lucide-react";
import { useId, useMemo, useState, type FormEvent } from "react";

import { displayError } from "../api/errors.js";
import { EmptyState, ErrorState, LoadingState } from "../components/states.js";
import { getRecordTypes } from "../records/records-api.js";
import { useShell } from "../shell/shell-context.js";
import { listStaff } from "../setup/setup-api.js";
import {
  archiveTaskDefinition,
  cancelOccurrence,
  listTaskDefinitions,
  listTaskOccurrences,
  recurrenceToRRule,
  rruleToFriendly,
  saveTaskDefinition,
  skipOccurrence,
  taskStatusTone,
  type FriendlyRecurrence,
  type TaskDefinition,
  type TaskKind,
  type TaskOccurrence,
} from "./tasks-api.js";

const weekdays = [
  ["MO", "Mon"],
  ["TU", "Tue"],
  ["WE", "Wed"],
  ["TH", "Thu"],
  ["FR", "Fri"],
  ["SA", "Sat"],
  ["SU", "Sun"],
] as const;

function isoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function taskRecurrenceLabel(task: TaskDefinition): string {
  if (!task.schedule) return "No schedule";
  if (task.schedule.one_off_due_at) {
    return `Once · ${new Intl.DateTimeFormat("en-NZ", { dateStyle: "medium", timeStyle: "short" }).format(new Date(task.schedule.one_off_due_at))}`;
  }
  const recurrence = rruleToFriendly(task.schedule.rrule);
  const interval =
    recurrence.interval > 1 ? `Every ${recurrence.interval} ` : "Every ";
  if (recurrence.frequency === "weekly")
    return `${interval}${recurrence.interval > 1 ? "weeks" : "week"} · ${recurrence.weekdays.join(", ")}`;
  if (recurrence.frequency === "monthly")
    return `${interval}${recurrence.interval > 1 ? "months" : "month"} · day ${recurrence.monthDay}`;
  return recurrence.interval === 1
    ? "Every day"
    : `Every ${recurrence.interval} days`;
}

function TaskEditor({
  current,
  locationId,
  onClose,
  onSaved,
}: {
  current?: TaskDefinition;
  locationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const id = useId();
  const friendly = rruleToFriendly(current?.schedule?.rrule);
  const [scheduleMode, setScheduleMode] = useState<"one_off" | "recurring">(
    current?.schedule?.one_off_due_at ? "one_off" : "recurring",
  );
  const [frequency, setFrequency] = useState<FriendlyRecurrence["frequency"]>(
    friendly.frequency,
  );
  const [taskKind, setTaskKind] = useState<TaskKind>(
    current?.task_kind ?? "log",
  );
  const staff = useQuery({
    queryFn: () => listStaff(locationId),
    queryKey: ["staff", locationId],
  });
  const types = useQuery({
    queryFn: getRecordTypes,
    queryKey: ["record-types"],
    staleTime: 300_000,
  });
  const save = useMutation({
    mutationFn: (form: FormData) => {
      let schedule: Record<string, unknown>;
      if (scheduleMode === "one_off") {
        schedule = {
          one_off_due_at: new Date(
            String(form.get("one_off_due_at")),
          ).toISOString(),
          reminder_offsets_min: [60],
        };
      } else {
        const interval = Number(form.get("interval") ?? 1);
        let recurrence: FriendlyRecurrence;
        if (frequency === "weekly") {
          const selected = form.getAll("weekday").map(String);
          if (selected.length === 0)
            throw new Error("Choose at least one weekday.");
          recurrence = { frequency, interval, weekdays: selected };
        } else if (frequency === "monthly") {
          recurrence = {
            frequency,
            interval,
            monthDay: Number(form.get("month_day") ?? 1),
          };
        } else {
          recurrence = { frequency, interval };
        }
        schedule = {
          active_from: String(form.get("active_from") ?? "") || null,
          active_to: String(form.get("active_to") ?? "") || null,
          due_window_end: String(form.get("due_window_end") ?? "") || null,
          due_window_start: String(form.get("due_window_start") ?? "") || null,
          reminder_offsets_min: [60],
          rrule: recurrenceToRRule(recurrence),
        };
      }
      return saveTaskDefinition(
        locationId,
        {
          assignee_staff_profile_id:
            String(form.get("assignee_staff_profile_id") ?? "") || null,
          instructions: String(form.get("instructions") ?? "").trim() || null,
          ...(current ? {} : { origin: "manager" }),
          photo_policy: String(form.get("photo_policy")),
          record_type_code:
            taskKind === "log"
              ? String(form.get("record_type_code") ?? "")
              : null,
          schedule,
          task_kind: taskKind,
          title: String(form.get("title") ?? "").trim(),
        },
        current,
      );
    },
    onSuccess: onSaved,
  });
  const oneOff = current?.schedule?.one_off_due_at
    ? new Date(
        new Date(current.schedule.one_off_due_at).getTime() -
          new Date(current.schedule.one_off_due_at).getTimezoneOffset() *
            60_000,
      )
        .toISOString()
        .slice(0, 16)
    : "";

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-stone-950/45 p-3 backdrop-blur-sm sm:p-6">
      <form
        aria-labelledby={`${id}-title`}
        aria-modal="true"
        className="mx-auto my-5 w-full max-w-3xl rounded-3xl bg-surface p-5 shadow-2xl sm:p-7"
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          save.mutate(new FormData(event.currentTarget));
        }}
        role="dialog"
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[0.68rem] font-bold tracking-widest text-emerald-800 uppercase">
              Task definition
            </p>
            <h2 className="mt-1 text-xl font-black" id={`${id}-title`}>
              {current ? "Edit task" : "Create a task"}
            </h2>
            <p className="mt-2 text-sm text-stone-500">
              Future occurrences use the new version; completed evidence keeps
              its original snapshot.
            </p>
          </div>
          <Button
            aria-label="Close task editor"
            onClick={onClose}
            size="icon"
            variant="ghost"
          >
            <X className="size-4" />
          </Button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-name`}>Task name</Label>
            <Input
              className="mt-1.5"
              defaultValue={current?.title ?? ""}
              id={`${id}-name`}
              name="title"
              placeholder="Check walk-in fridge temperature"
              required
            />
          </div>
          <div>
            <Label htmlFor={`${id}-kind`}>Task type</Label>
            <select
              className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
              id={`${id}-kind`}
              name="task_kind"
              onChange={(event) => setTaskKind(event.target.value as TaskKind)}
              value={taskKind}
            >
              <option value="log">Log a record</option>
              <option value="check">Check</option>
              <option value="cleaning">Cleaning</option>
              <option value="maintenance">Maintenance</option>
              <option value="training">Training</option>
              <option value="reminder_follow_up">Reminder follow-up</option>
            </select>
          </div>
          {taskKind === "log" ? (
            <div>
              <Label htmlFor={`${id}-record`}>Record form</Label>
              <select
                className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                defaultValue={current?.record_type_code ?? ""}
                id={`${id}-record`}
                name="record_type_code"
                required
              >
                <option value="">Choose a record form</option>
                {types.data?.map((type) => (
                  <option key={type.code} value={type.code}>
                    {type.title}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div>
            <Label htmlFor={`${id}-assignee`}>Assigned person</Label>
            <select
              className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
              defaultValue={current?.assignee_staff_profile_id ?? ""}
              id={`${id}-assignee`}
              name="assignee_staff_profile_id"
            >
              <option value="">Anyone at this location</option>
              {staff.data?.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.display_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor={`${id}-photo`}>Photo request</Label>
            <select
              className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
              defaultValue={current?.photo_policy ?? "optional"}
              id={`${id}-photo`}
              name="photo_policy"
            >
              <option value="optional">Optional</option>
              <option value="requested">Request a photo</option>
              <option value="required_by_business">
                Required by this business
              </option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor={`${id}-instructions`}>Instructions</Label>
            <textarea
              className="mt-1.5 min-h-20 w-full rounded-xl border border-stone-300 bg-surface p-3 text-sm"
              defaultValue={current?.instructions ?? ""}
              id={`${id}-instructions`}
              name="instructions"
            />
          </div>
        </div>

        <fieldset className="mt-6 rounded-2xl border border-stone-200 bg-surface p-4 sm:p-5">
          <legend className="px-1 text-sm font-black">When is it due?</legend>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              className={cn(
                "rounded-xl border px-3 py-3 text-sm font-bold",
                scheduleMode === "recurring"
                  ? "border-emerald-800 bg-emerald-50 text-emerald-950"
                  : "border-stone-200",
              )}
              onClick={() => setScheduleMode("recurring")}
              type="button"
            >
              Recurring
            </button>
            <button
              className={cn(
                "rounded-xl border px-3 py-3 text-sm font-bold",
                scheduleMode === "one_off"
                  ? "border-emerald-800 bg-emerald-50 text-emerald-950"
                  : "border-stone-200",
              )}
              onClick={() => setScheduleMode("one_off")}
              type="button"
            >
              One-off
            </button>
          </div>
          {scheduleMode === "one_off" ? (
            <div className="mt-4">
              <Label htmlFor={`${id}-once`}>Due date and time</Label>
              <Input
                className="mt-1.5"
                defaultValue={oneOff}
                id={`${id}-once`}
                name="one_off_due_at"
                required
                type="datetime-local"
              />
            </div>
          ) : (
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor={`${id}-frequency`}>Frequency</Label>
                <select
                  className="mt-1.5 h-11 w-full rounded-xl border border-stone-300 bg-surface px-3 text-sm"
                  id={`${id}-frequency`}
                  onChange={(event) =>
                    setFrequency(
                      event.target.value as FriendlyRecurrence["frequency"],
                    )
                  }
                  value={frequency}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              <div>
                <Label htmlFor={`${id}-interval`}>Repeat every</Label>
                <div className="mt-1.5 flex items-center gap-2">
                  <Input
                    defaultValue={friendly.interval}
                    id={`${id}-interval`}
                    min="1"
                    name="interval"
                    required
                    type="number"
                  />
                  <span className="text-xs font-semibold text-stone-500">
                    {frequency === "daily"
                      ? "day(s)"
                      : frequency === "weekly"
                        ? "week(s)"
                        : "month(s)"}
                  </span>
                </div>
              </div>
              {frequency === "weekly" ? (
                <div className="sm:col-span-2">
                  <Label>Weekdays</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {weekdays.map(([value, label]) => (
                      <label className="cursor-pointer" key={value}>
                        <input
                          className="peer sr-only"
                          defaultChecked={
                            friendly.frequency === "weekly"
                              ? friendly.weekdays.includes(value)
                              : value === "MO"
                          }
                          name="weekday"
                          type="checkbox"
                          value={value}
                        />
                        <span className="grid size-10 place-items-center rounded-xl border border-stone-300 bg-surface text-xs font-bold peer-checked:border-emerald-800 peer-checked:bg-emerald-800 peer-checked:text-white">
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}
              {frequency === "monthly" ? (
                <div>
                  <Label htmlFor={`${id}-monthday`}>Day of month</Label>
                  <Input
                    className="mt-1.5"
                    defaultValue={
                      friendly.frequency === "monthly" ? friendly.monthDay : 1
                    }
                    id={`${id}-monthday`}
                    max="31"
                    min="1"
                    name="month_day"
                    type="number"
                  />
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-2 sm:col-span-2">
                <div>
                  <Label htmlFor={`${id}-window-start`}>Due from</Label>
                  <Input
                    className="mt-1.5"
                    defaultValue={current?.schedule?.due_window_start ?? ""}
                    id={`${id}-window-start`}
                    name="due_window_start"
                    type="time"
                  />
                </div>
                <div>
                  <Label htmlFor={`${id}-window-end`}>Due by</Label>
                  <Input
                    className="mt-1.5"
                    defaultValue={current?.schedule?.due_window_end ?? ""}
                    id={`${id}-window-end`}
                    name="due_window_end"
                    type="time"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor={`${id}-active-from`}>Starts on</Label>
                <Input
                  className="mt-1.5"
                  defaultValue={
                    current?.schedule?.active_from ?? isoDate(new Date())
                  }
                  id={`${id}-active-from`}
                  name="active_from"
                  type="date"
                />
              </div>
              <div>
                <Label htmlFor={`${id}-active-to`}>Ends on (optional)</Label>
                <Input
                  className="mt-1.5"
                  defaultValue={current?.schedule?.active_to ?? ""}
                  id={`${id}-active-to`}
                  name="active_to"
                  type="date"
                />
              </div>
            </div>
          )}
        </fieldset>
        {save.error ? (
          <p
            className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-800"
            role="alert"
          >
            {displayError(save.error) ===
              "Something went wrong. Please try again." &&
            save.error instanceof Error
              ? save.error.message
              : displayError(save.error)}
          </p>
        ) : null}
        <div className="mt-6 flex justify-end gap-2">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={save.isPending} type="submit">
            {save.isPending
              ? "Saving…"
              : current
                ? "Save new version"
                : "Create task"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Definitions({ locationId }: { locationId: string }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<TaskDefinition | "new" | null>(null);
  const tasks = useQuery({
    queryFn: () => listTaskDefinitions(locationId),
    queryKey: ["task-definitions", locationId],
  });
  const archive = useMutation({
    mutationFn: archiveTaskDefinition,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["task-definitions", locationId],
      }),
  });
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditing("new")}>
          <Plus className="size-4" /> Create task
        </Button>
      </div>
      {tasks.isPending ? (
        <LoadingState label="Loading task definitions" />
      ) : null}
      {tasks.error ? (
        <ErrorState
          message={displayError(tasks.error)}
          onAction={() => void tasks.refetch()}
          title="Task definitions aren’t available"
        />
      ) : null}
      {tasks.data?.items.length === 0 ? (
        <EmptyState
          description="Create recurring checks, one-off reminders, cleaning, maintenance, or training tasks."
          title="No task definitions yet"
        />
      ) : null}
      {tasks.data?.items.length ? (
        <Card className="overflow-hidden">
          <div className="divide-y divide-stone-100">
            {tasks.data.items.map((task) => (
              <div
                className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:px-5"
                key={task.id}
              >
                <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-stone-100 text-stone-600">
                  <ClipboardList className="size-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-black">{task.title}</p>
                    <Badge
                      tone={task.status === "active" ? "positive" : "neutral"}
                    >
                      {task.status}
                    </Badge>
                    <Badge>
                      {task.origin === "plan_generated"
                        ? "From plan"
                        : "Manager"}
                    </Badge>
                  </div>
                  <p className="mt-1 truncate text-xs text-stone-500">
                    {taskRecurrenceLabel(task)} · version {task.current_version}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button
                    onClick={() => setEditing(task)}
                    size="sm"
                    variant="ghost"
                  >
                    <Pencil className="size-3.5" /> Edit
                  </Button>
                  <Button
                    disabled={archive.isPending}
                    onClick={() => {
                      if (
                        globalThis.confirm(
                          "Archive this task? Future occurrences will be cancelled.",
                        )
                      )
                        archive.mutate(task.id);
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <Archive className="size-3.5" /> Archive
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}
      {archive.error ? (
        <p className="text-sm font-semibold text-red-800">
          {displayError(archive.error)}
        </p>
      ) : null}
      {editing ? (
        <TaskEditor
          {...(editing === "new" ? {} : { current: editing })}
          locationId={locationId}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await queryClient.invalidateQueries({
              queryKey: ["task-definitions", locationId],
            });
            setEditing(null);
          }}
        />
      ) : null}
    </div>
  );
}

function OccurrenceBoard({
  canManage,
  locationId,
}: {
  canManage: boolean;
  locationId: string;
}) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TaskOccurrence["status"] | "">("");
  const from = isoDate(new Date());
  const end = new Date();
  end.setDate(end.getDate() + 14);
  const occurrences = useQuery({
    queryFn: () =>
      listTaskOccurrences(locationId, {
        assignee: canManage ? "any" : "me",
        from,
        ...(status ? { status } : {}),
        to: isoDate(end),
      }),
    queryKey: ["task-occurrences", locationId, canManage, status],
  });
  const skip = useMutation({
    mutationFn: (id: string) => skipOccurrence(id),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["task-occurrences", locationId],
      }),
  });
  const cancel = useMutation({
    mutationFn: cancelOccurrence,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["task-occurrences", locationId],
      }),
  });
  const groups = useMemo(() => {
    const grouped = new Map<string, TaskOccurrence[]>();
    for (const item of occurrences.data?.items ?? []) {
      grouped.set(item.local_date, [
        ...(grouped.get(item.local_date) ?? []),
        item,
      ]);
    }
    return [...grouped.entries()];
  }, [occurrences.data]);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-stone-500">
          Today and the next 14 days ·{" "}
          {canManage ? "all assignees" : "your tasks"}
        </p>
        <select
          aria-label="Task status"
          className="h-10 rounded-xl border border-stone-300 bg-surface px-3 text-sm"
          onChange={(event) =>
            setStatus(event.target.value as TaskOccurrence["status"] | "")
          }
          value={status}
        >
          <option value="">All statuses</option>
          <option value="due">Due</option>
          <option value="upcoming">Upcoming</option>
          <option value="completed">Completed</option>
          <option value="missed">Missed</option>
          <option value="skipped">Skipped</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      {occurrences.isPending ? (
        <LoadingState label="Loading the occurrence board" />
      ) : null}
      {occurrences.error ? (
        <ErrorState
          message={displayError(occurrences.error)}
          onAction={() => void occurrences.refetch()}
          title="The task board isn’t available"
        />
      ) : null}
      {occurrences.data?.items.length === 0 ? (
        <EmptyState
          calm
          description="Published plan and manager task schedules appear here as occurrences are materialised."
          title="No tasks in this window"
        />
      ) : null}
      <div className="space-y-5">
        {groups.map(([date, items]) => (
          <section key={date}>
            <h3 className="mb-2 text-xs font-black tracking-wider text-stone-400 uppercase">
              {new Intl.DateTimeFormat("en-NZ", { dateStyle: "full" }).format(
                new Date(`${date}T12:00:00`),
              )}
            </h3>
            <Card className="overflow-hidden">
              <div className="divide-y divide-stone-100">
                {items?.map((item) => (
                  <div
                    className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center"
                    key={item.id}
                  >
                    <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-stone-100 text-stone-600">
                      <CheckSquare2 className="size-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="truncate text-sm font-black">
                          {item.title}
                        </p>
                        <Badge tone={taskStatusTone(item.status)}>
                          {item.status}
                        </Badge>
                      </div>
                      <p className="mt-1 text-xs text-stone-500">
                        Due{" "}
                        {new Intl.DateTimeFormat("en-NZ", {
                          timeStyle: "short",
                        }).format(new Date(item.due_at))}
                        {item.assignee_staff_profile_id
                          ? ` · assigned ${item.assignee_staff_profile_id}`
                          : " · anyone"}
                      </p>
                    </div>
                    {["due", "upcoming"].includes(item.status) ? (
                      <div className="flex gap-1">
                        {item.record_type_code ? (
                          <Link
                            className="inline-flex h-9 items-center rounded-lg bg-emerald-800 px-3 text-xs font-semibold text-white hover:bg-emerald-900"
                            search={{
                              record_type_code: item.record_type_code,
                              task_occurrence_id: item.id,
                            }}
                            to="/log"
                          >
                            Log now
                          </Link>
                        ) : null}
                        <Button
                          disabled={skip.isPending}
                          onClick={() => {
                            if (
                              globalThis.confirm(
                                "Skip this task? Managers will see it in Needs attention.",
                              )
                            )
                              skip.mutate(item.id);
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          <SkipForward className="size-3.5" /> Skip
                        </Button>
                        {canManage ? (
                          <Button
                            disabled={cancel.isPending}
                            onClick={() => cancel.mutate(item.id)}
                            size="sm"
                            variant="ghost"
                          >
                            <X className="size-3.5" /> Cancel
                          </Button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </Card>
          </section>
        ))}
      </div>
      {skip.error || cancel.error ? (
        <p className="text-sm font-semibold text-red-800">
          {displayError(skip.error ?? cancel.error)}
        </p>
      ) : null}
    </div>
  );
}

export function TasksWorkspacePage() {
  const shell = useShell();
  const queryClient = useQueryClient();
  const canManage = shell.activeRole !== "staff";
  const locationId = shell.activeLocation?.id;
  const [tab, setTab] = useState<"definitions" | "schedule">(
    canManage ? "definitions" : "schedule",
  );
  return (
    <>
      <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[0.68rem] font-bold tracking-[0.14em] text-emerald-800 uppercase">
            Schedule
          </p>
          <h1 className="mt-2 text-3xl font-black tracking-[-0.045em] text-stone-950 sm:text-4xl">
            Tasks
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-stone-600">
            Turn plan obligations and manager reminders into clear work without
            blocking the kitchen.
          </p>
        </div>
        <Button
          disabled={!locationId}
          onClick={() =>
            void queryClient.invalidateQueries({
              queryKey: ["task-occurrences", locationId],
            })
          }
          variant="outline"
        >
          <RefreshCw className="size-4" /> Refresh window
        </Button>
      </header>
      {!locationId ? (
        <EmptyState
          description="Choose a location to manage its task schedule."
          title="Choose a location"
        />
      ) : (
        <>
          <div className="mb-5 flex gap-2 border-b border-stone-200">
            {canManage ? (
              <button
                className={cn(
                  "border-b-2 px-3 py-3 text-sm font-bold",
                  tab === "definitions"
                    ? "border-emerald-800 text-emerald-900"
                    : "border-transparent text-stone-500",
                )}
                onClick={() => setTab("definitions")}
                type="button"
              >
                <ClipboardList className="mr-2 inline size-4" /> Definitions
              </button>
            ) : null}
            <button
              className={cn(
                "border-b-2 px-3 py-3 text-sm font-bold",
                tab === "schedule"
                  ? "border-emerald-800 text-emerald-900"
                  : "border-transparent text-stone-500",
              )}
              onClick={() => setTab("schedule")}
              type="button"
            >
              <CalendarClock className="mr-2 inline size-4" /> Occurrence board
            </button>
          </div>
          {tab === "definitions" && canManage ? (
            <Definitions locationId={locationId} />
          ) : (
            <OccurrenceBoard canManage={canManage} locationId={locationId} />
          )}
        </>
      )}
    </>
  );
}
